import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ComplianceStatePanel from '../components/cdp/ComplianceStatePanel';
import CDPRevisionHistory from '../components/cdp/CDPRevisionHistory';
import ParentNotificationInbox from '../components/parent/ParentNotificationInbox';
import {
  Brain, Compass, BookOpen, Briefcase, GraduationCap, Award,
  CheckCircle2, Circle, Clock, AlertCircle, Target, Layers,
  ChevronDown, ChevronRight, ChevronLeft, ShieldCheck, RefreshCw, User,
  BarChart3, Sparkles, ArrowRight, Map, Eye, School,
  TrendingUp, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompletionBar = ({ pct, color = 'from-cyan-500 to-blue-500' }) => (
  <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${pct}%` }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`h-full rounded-full bg-gradient-to-r ${color}`}
    />
  </div>
);

const STATUS_CONFIG = {
  complete:       { label: 'Complete',     color: 'text-emerald-400', bg: 'bg-emerald-500/12 border-emerald-500/25', dot: 'bg-emerald-400' },
  in_progress:    { label: 'In Progress',  color: 'text-amber-400',   bg: 'bg-amber-500/12 border-amber-500/25',   dot: 'bg-amber-400' },
  not_started:    { label: 'Not Started',  color: 'text-slate-300',   bg: 'bg-slate-700/20 border-slate-600/20',   dot: 'bg-slate-500' },
  ready_for_review: { label: 'Ready for Review', color: 'text-cyan-400', bg: 'bg-cyan-500/12 border-cyan-500/25', dot: 'bg-cyan-400' },
  revision_due:   { label: 'Revision Due', color: 'text-orange-400',  bg: 'bg-orange-500/12 border-orange-500/25', dot: 'bg-orange-400' },
};
const sc = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.not_started;

const StatusPill = ({ status }) => {
  const s = sc(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${s.bg} ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const CheckItem = ({ label, done }) => (
  <div className={`flex items-center gap-3 p-2.5 rounded-lg border ${
    done ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-slate-800/40 border-slate-700/30'
  }`}>
    {done
      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
      : <Circle className="w-4 h-4 text-slate-300 shrink-0" />
    }
    <span className={`text-sm ${done ? 'text-emerald-300' : 'text-slate-300'}`}>{label}</span>
  </div>
);

const SectionCard = ({ icon: Icon, label, tagline, done, expanded, onToggle, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className={`rounded-xl border overflow-hidden ${done ? 'border-emerald-500/20 bg-emerald-500/4' : 'border-slate-700/40 bg-slate-900/40'}`}
  >
    <button
      className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
      onClick={onToggle}
    >
      <div className="w-9 h-9 rounded-xl bg-slate-800/60 flex items-center justify-center shrink-0 border border-slate-700/40">
        <Icon className="w-4 h-4 text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm">{label}</p>
        <p className="text-slate-300 text-[11px] mt-0.5">{tagline}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {done && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
      </div>
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-1 border-t border-white/[0.04]">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export default function ParentCDPViewPage() {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ career: true, academic: false, experiences: false, compliance: false });
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const headers = getAuthHeader();
        // First get linked children
        const childRes = await axios.get(`${API}/student-context/list-children`, headers);
        const childList = childRes.data.items || [];
        setChildren(childList);
        if (childList.length > 0 && !selectedChild) {
          setSelectedChild(childList[0].child_id);
        }
      } catch {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!selectedChild) return;
    const load = async () => {
      try {
        setLoading(true);
        const headers = getAuthHeader();
        const params = { student_id: selectedChild };
        const res = await axios.get(`${API}/nc/cdp/parent-view`, { ...headers, params });
        setData(res.data);
      } catch (err) {
        // If no parent-child link, show placeholder
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedChild]);

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const handleAcknowledge = async () => {
    if (!selectedChild || acknowledged) return;
    setAcknowledging(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/institution/parent-acknowledgment/${selectedChild}`, {}, headers);
      setAcknowledged(true);
      // Refresh data to update completion
      const res = await axios.get(`${API}/nc/cdp/parent-view`, { ...headers, params: { student_id: selectedChild } });
      setData(res.data);
    } catch { /* silent */ }
    finally { setAcknowledging(false); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto px-4 py-12 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin mx-auto mb-3" />
            <p className="text-slate-300 text-sm">Loading your child's plan…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!data && children.length === 0) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center p-8 rounded-2xl border border-slate-700/40 bg-slate-900/40">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-white font-bold text-xl mb-2">No linked child account</h2>
            <p className="text-slate-300 text-sm mb-4">
              To view your child's FuturePath plan, they need to have a FuturePath account linked to your family.
            </p>
            <Link
              to="/app/family"
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Go to Family Dashboard
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  const overallPct = data?.overall_completion || 0;
  const cdpStatus = overallPct >= 80 ? 'complete' : overallPct >= 40 ? 'in_progress' : 'not_started';
  const studentName = data?.student_name || children.find(c => c.child_id === selectedChild)?.name || 'Your Child';
  const grade = data?.grade;
  const school = data?.school;
  const wblPct = data?.wbl_goal ? Math.min(100, Math.round((data.wbl_hours || 0) / data.wbl_goal * 100)) : 0;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="parent-cdp-view-page">

        {/* ── Back to parent dashboard ── */}
        <Link
          to="/app/family"
          className="inline-flex items-center gap-2 text-[12px] font-medium text-slate-300
            hover:text-white transition-colors mb-5"
          data-testid="back-to-parent-dashboard">
          <ChevronLeft className="h-3.5 w-3.5" />
          Back to my dashboard
        </Link>

        {/* ── Read-only banner ── */}
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-blue-500/8 border border-blue-500/20">
          <Eye className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <p className="text-[11px] text-blue-300">Read-only view, your child owns and builds this plan themselves.</p>
        </div>

        {/* ── Child selector (prominent, at top) ── */}
        {children.length > 1 && (
          <div className="mb-5 p-4 rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.06] to-violet-500/[0.04]"
            data-testid="child-switcher-panel"
            style={{ boxShadow: '0 20px 60px -50px rgba(34,211,238,0.5)' }}>
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-cyan-300" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-cyan-200">Viewing plan for</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {children.map(c => {
                const active = selectedChild === c.child_id;
                return (
                  <button
                    key={c.child_id}
                    onClick={() => setSelectedChild(c.child_id)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                      active
                        ? 'bg-cyan-500/20 text-white border-cyan-400/50'
                        : 'bg-slate-800/50 text-slate-300 border-slate-700/40 hover:bg-slate-800 hover:border-slate-600/60'
                    }`}
                    style={active ? { boxShadow: '0 0 22px -8px rgba(34,211,238,0.65)' } : {}}
                    data-testid={`child-selector-${c.child_id}`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      active ? 'bg-cyan-400/25 text-cyan-100' : 'bg-slate-700/50 text-slate-300'
                    }`}>
                      {(c.name || 'S')[0].toUpperCase()}
                    </span>
                    <div className="text-left">
                      <div className="text-[13px] leading-none">{c.name || c.email}</div>
                      {c.grade_label && <div className="text-[10px] text-slate-300 mt-0.5">{c.grade_label}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Parent notification inbox, required by NC § 115C-158.10 */}
        <ParentNotificationInbox studentId={selectedChild} />

        {/* NC state compliance for this student */}
        <div className="mb-5">
          <ComplianceStatePanel studentId={selectedChild} />
        </div>

        {/* Every change made to this student's CDP, with source and actor */}
        <div className="mb-5">
          <CDPRevisionHistory studentId={selectedChild} canRequestReview={true} />
        </div>

        {/* ── Header ── */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-cyan-400 font-semibold mb-1">
                My Child's FuturePath
              </p>
              <h1 className="text-2xl font-bold text-white">{studentName}'s Plan</h1>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {grade && (
                  <span className="text-xs text-slate-300 flex items-center gap-1">
                    <GraduationCap className="w-3 h-3" /> Grade {grade}
                  </span>
                )}
                {school && (
                  <span className="text-xs text-slate-300 flex items-center gap-1">
                    <School className="w-3 h-3" /> {school}
                  </span>
                )}
                {data?.framework && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20">
                    {data.state} {data.framework}
                  </span>
                )}
              </div>
            </div>
            <StatusPill status={cdpStatus} />
          </div>

          {/* Overall progress bar */}
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-700/40">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">Plan Completion</span>
              <span className="text-2xl font-black text-cyan-400">{overallPct}%</span>
            </div>
            <CompletionBar pct={overallPct} color="from-cyan-500 via-blue-500 to-violet-500" />
            {data?.last_synced && (
              <p className="text-[10px] text-slate-300 mt-2">
                Last updated: {new Date(data.last_synced).toLocaleDateString()}
              </p>
            )}
          </div>
        </motion.div>

        {/* ── Quick progress checklist ── */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <CheckItem label="Strengths Assessment" done={data?.has_assessment} />
          <CheckItem label="Career Direction Set" done={data?.sections_summary?.career_direction} />
          <CheckItem label="4-Year Course Plan" done={data?.sections_summary?.academic_plan} />
          <CheckItem label={`WBL Progress (${Math.round(data?.wbl_hours || 0)}h)`} done={data?.sections_summary?.wbl_progress} />
        </div>

        {/* ── Sections ── */}
        <div className="space-y-3">

          {/* Career Direction */}
          <SectionCard
            icon={Compass}
            label="Career Direction"
            tagline="What they're exploring and where they may be headed"
            done={!!data?.career_goal}
            expanded={expanded.career}
            onToggle={() => toggle('career')}
          >
            {data?.career_goal ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/60 border border-slate-700/40">
                  <Target className="w-4 h-4 text-cyan-400 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-300 uppercase tracking-wide">Career Interest</p>
                    <p className="text-white font-semibold text-sm">{data.career_goal}</p>
                  </div>
                </div>
                {data?.cte_pathway && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/60 border border-slate-700/40">
                    <Layers className="w-4 h-4 text-violet-400 shrink-0" />
                    <div>
                      <p className="text-[10px] text-slate-300 uppercase tracking-wide">CTE Pathway (AI-matched)</p>
                      <p className="text-white font-semibold text-sm">{data.cte_pathway.name}</p>
                      {data.cte_pathway.confidence && (
                        <p className="text-[10px] text-slate-300">Confidence: {data.cte_pathway.confidence}</p>
                      )}
                    </div>
                  </div>
                )}
                {data?.assessment_snapshot?.type_label && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/60 border border-slate-700/40">
                    <Brain className="w-4 h-4 text-amber-400 shrink-0" />
                    <div>
                      <p className="text-[10px] text-slate-300 uppercase tracking-wide">Personality Type</p>
                      <p className="text-white font-semibold text-sm">{data.assessment_snapshot.type_label}</p>
                      {data.assessment_snapshot.riasec_code && (
                        <p className="text-[10px] text-slate-300">Code: {data.assessment_snapshot.riasec_code}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-3 text-center">
                <Compass className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                <p className="text-slate-300 text-sm">Still exploring, no career direction set yet.</p>
                <p className="text-[11px] text-slate-300 mt-1">This fills in automatically as they use FuturePath.</p>
              </div>
            )}
          </SectionCard>

          {/* Academic Path */}
          <SectionCard
            icon={BookOpen}
            label="Academic Path"
            tagline="4-year course planning and academic direction"
            done={!!data?.sections_summary?.academic_plan}
            expanded={expanded.academic}
            onToggle={() => toggle('academic')}
          >
            {data?.sections_summary?.academic_plan ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/8 border border-emerald-500/20">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <p className="text-emerald-300 text-sm">4-year course plan created</p>
                </div>
                <p className="text-[11px] text-slate-300 mt-2">
                  The full course plan is visible only to {studentName?.split(' ')[0]} and their counselor.
                </p>
              </div>
            ) : (
              <div className="py-3 text-center">
                <BookOpen className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                <p className="text-slate-300 text-sm">No course plan yet.</p>
                <p className="text-[11px] text-slate-300 mt-1">They can build one in FuturePath's Course Planner.</p>
              </div>
            )}
          </SectionCard>

          {/* Work-Based Learning */}
          <SectionCard
            icon={Briefcase}
            label="Experiences & WBL"
            tagline="Work-based learning hours and real-world experiences"
            done={(data?.wbl_hours || 0) > 0}
            expanded={expanded.experiences}
            onToggle={() => toggle('experiences')}
          >
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-slate-300">Work-Based Learning Hours</p>
                  <p className="text-sm font-bold text-white">
                    {Math.round(data?.wbl_hours || 0)}h / {data?.wbl_goal || 40}h goal
                  </p>
                </div>
                <CompletionBar
                  pct={wblPct}
                  color={wblPct >= 100 ? 'from-emerald-500 to-teal-400' : wblPct > 0 ? 'from-amber-500 to-yellow-400' : 'from-slate-600 to-slate-500'}
                />
                <p className="text-[10px] text-slate-300 mt-1">
                  {wblPct >= 100 ? 'Goal reached!' : wblPct > 0 ? `${100 - wblPct}% remaining to reach goal` : 'No hours logged yet'}
                </p>
              </div>
              {(data?.wbl_hours || 0) === 0 && (
                <p className="text-[11px] text-slate-300 italic">
                  WBL includes job shadows, internships, apprenticeships, and mentorships.
                </p>
              )}
            </div>
          </SectionCard>

          {/* State Compliance Summary */}
          <SectionCard
            icon={ShieldCheck}
            label={`${data?.framework || 'Career Development Plan'} Status`}
            tagline="How this plan maps to state requirements"
            done={overallPct >= 60}
            expanded={expanded.compliance}
            onToggle={() => toggle('compliance')}
          >
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/40">
                <p className="text-[11px] text-slate-300 mb-2 uppercase tracking-wide">Compliance Progress</p>
                <div className="flex items-center gap-3">
                  <CompletionBar
                    pct={overallPct}
                    color={overallPct >= 80 ? 'from-emerald-500 to-teal-400' : 'from-amber-500 to-yellow-400'}
                  />
                  <span className="text-sm font-bold text-white shrink-0">{overallPct}%</span>
                </div>
              </div>
              {data?.past_snapshots > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/8 border border-blue-500/20">
                  <RefreshCw className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <p className="text-[11px] text-blue-300">
                    {data.past_snapshots} saved version{data.past_snapshots !== 1 ? 's' : ''}, plan history is being tracked.
                  </p>
                </div>
              )}
              <div className="flex items-start gap-2 mt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" />
                <p className="text-[10px] text-slate-300">
                  This plan auto-populates from {studentName?.split(' ')[0]}'s activity in FuturePath,                   no separate forms needed. The system handles {data?.state || 'state'} compliance requirements automatically.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* ── NC G.S. § 115C-174.26(b), Parent Acknowledgment ── */}
        {!acknowledged ? (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-500/6"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white mb-1">Acknowledge This Plan</p>
                <p className="text-xs text-slate-300 mb-3">
                  NC G.S. § 115C-174.26(b) requires a parent or guardian to review and acknowledge their child's
                  Career Development Plan annually. Tap below to record your acknowledgment.
                </p>
                <button
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                  data-testid="acknowledge-cdp-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg text-sm font-semibold hover:bg-amber-500/30 disabled:opacity-50 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {acknowledging ? 'Recording…' : `I've reviewed ${studentName?.split(' ')[0]}'s plan`}
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-4 p-4 rounded-xl border border-emerald-500/25 bg-emerald-500/6 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-300">Plan Acknowledged</p>
              <p className="text-xs text-slate-300 mt-0.5">
                NC G.S. § 115C-174.26(b) satisfied, your acknowledgment has been recorded and logged.
              </p>
            </div>
          </motion.div>
        )}

        {/* ── Footer message ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 p-4 rounded-xl bg-gradient-to-br from-slate-800/60 to-slate-900/40 border border-slate-700/40"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white mb-1">How this plan works</p>
              <p className="text-xs text-slate-300">
                {studentName?.split(' ')[0]}'s FuturePath plan builds itself as they explore careers,
                complete their assessment, and plan coursework. There's nothing to fill out,                 the plan is generated automatically from real activity.
              </p>
              <p className="text-[11px] text-slate-300 mt-2">
                Parents see a summary view. The full interactive plan belongs to {studentName?.split(' ')[0]}.
              </p>
            </div>
          </div>
        </motion.div>

      </div>
    </AppLayout>
  );
}
